import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { formatCurrency } from "../../CMCalculatorV2/utils/formatCurrency";
import { buildPacingData } from "../utils/pacingData";
import usePageColorScheme from "../../shared/hooks/usePageColorScheme";

const DEEP_GREEN = "#1e7e34";
const LIGHT_GREEN = "#5cb85c";
const RED = "#FF595E";
const GREY_DASH = "#adb5bd";
const BAND_GREEN = "rgba(92, 184, 92, 0.10)";
const TODAY_COLOR = "#1982C4";
const LIGHT_CHART_COLORS = { grid: "#f1f1f1", tick: "#717680", axis: "#e9eaeb", cursor: "#d0d5dd", surface: "#fff" };
const DARK_CHART_TOKENS = { grid: "--od-c-f0f0f0", tick: "--od-c-717680", axis: "--od-c-e9eaeb", cursor: "--od-c-d1d5db", surface: "--od-c-ffffff" };

const readDarkChartColors = () => {
  const styles = window.getComputedStyle(document.body);
  return Object.fromEntries(
    Object.entries(DARK_CHART_TOKENS).map(([key, token]) => [key, styles.getPropertyValue(token).trim() || LIGHT_CHART_COLORS[key]])
  );
};

const formatCompact = (value) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}k`;
  return `$${value}`;
};

const CustomTooltip = ({ active, payload, forecastColor }) => {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div
      style={{
        backgroundColor: "var(--od-c-ffffff, #fff)",
        border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow:
          "0px 4px 6px -2px rgba(10,13,18,0.03), 0px 12px 16px -4px rgba(10,13,18,0.08)",
        fontSize: 13,
        lineHeight: "20px",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--od-c-333333, #333)" }}>
        {point.label}
        {point.isToday && (
          <span style={{ color: TODAY_COLOR, fontWeight: 500 }}> (Today)</span>
        )}
        {point.isSettlement && (
          <span style={{ color: GREY_DASH, fontWeight: 500 }}>
            {" "}
            Settlement
          </span>
        )}
      </div>
      {point.actual !== null && (
        <div style={{ color: point.actual < point.linearPace ? RED : DEEP_GREEN }}>
          Actual: {formatCurrency(point.actual)}
        </div>
      )}
      {point.forecast !== null && (
        <div style={{ color: forecastColor || LIGHT_GREEN }}>
          Forecast: {formatCurrency(point.forecast)}
        </div>
      )}
      <div style={{ color: GREY_DASH }}>
        Target Pace: {formatCurrency(point.linearPace)}
      </div>
      {point.dailyRevenue > 0 && (
        <div style={{ color: "var(--od-c-535862, #535862)" }}>
          Day Revenue: {formatCurrency(point.dailyRevenue)}
        </div>
      )}
    </div>
  );
};

const DailyTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload;
  if (!point) return null;

  return (
    <div
      style={{
        backgroundColor: "var(--od-c-ffffff, #fff)",
        border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
        borderRadius: 8,
        padding: "10px 14px",
        boxShadow:
          "0px 4px 6px -2px rgba(10,13,18,0.03), 0px 12px 16px -4px rgba(10,13,18,0.08)",
        fontSize: 13,
        lineHeight: "20px",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: "var(--od-c-333333, #333)" }}>
        {point.label}
        {point.isToday && (
          <span style={{ color: TODAY_COLOR, fontWeight: 500 }}> (Today)</span>
        )}
      </div>
      <div style={{ color: point.actual !== null ? DEEP_GREEN : LIGHT_GREEN }}>
        Revenue: {formatCurrency(point.dailyRevenue)}
      </div>
    </div>
  );
};

const RevenuePacingChart = ({ shows, venue, month, year, isCurrentMonth }) => {
  const [view, setView] = useState("cumulative");
  const colorScheme = usePageColorScheme();
  const chartColors = useMemo(
    () => (colorScheme === "dark" ? readDarkChartColors() : LIGHT_CHART_COLORS),
    [colorScheme]
  );

  const pacing = useMemo(
    () => buildPacingData(shows, venue, month, year, isCurrentMonth),
    [shows, venue, month, year, isCurrentMonth]
  );

  if (!shows || shows.length === 0) return null;

  const { data, monthlyTarget, actualToDate, forecastFinal, targetCrossDay, todayDay, isPastMonth, isFutureMonth, dailyTargetPace } = pacing;

  const monthLabel = new Date(year, month).toLocaleString("default", {
    month: "long",
  });

  const vsTarget = forecastFinal - monthlyTarget;
  const vsTargetPct = Math.round((forecastFinal / monthlyTarget) * 100);
  const hasTarget = monthlyTarget > 0;
  const vsColor = !hasTarget
    ? "var(--od-c-535862, #535862)"
    : vsTargetPct >= 100 ? "#28a745" : vsTargetPct >= 75 ? "#ffc107" : "#FF595E";
  const forecastHitsTarget = forecastFinal >= monthlyTarget;
  const forecastColor = forecastHitsTarget ? LIGHT_GREEN : RED;
  const bandColor = forecastHitsTarget ? BAND_GREEN : "rgba(255, 89, 94, 0.10)";

  const yMax = Math.max(
    monthlyTarget * 1.3,
    ...data.map((d) => Math.max(d.actual || 0, d.forecast || 0, d.confidenceUpper || 0))
  );

  const dailyMax = Math.max(
    dailyTargetPace * 2,
    ...data.map((d) => d.dailyRevenue)
  );

  const tickInterval = Math.max(1, Math.floor(data.length / 8));

  return (
    <div
      style={{
        backgroundColor: "var(--od-c-ffffff, #fff)",
        border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
        borderRadius: 12,
        boxShadow: "0px 1px 2px 0px rgba(10,13,18,0.05)",
        marginBottom: 20,
        padding: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: "var(--od-c-333333, #333)" }}>
            {monthLabel}
          </span>
          {isCurrentMonth && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: "#1982C4",
                backgroundColor: "rgba(25,130,196,0.08)",
                padding: "2px 8px",
                borderRadius: 9999,
                lineHeight: "18px",
              }}
            >
              Current
            </span>
          )}
        </div>
        <div
          style={{
            display: "flex",
            backgroundColor: "var(--od-c-fafafa, #fafafa)",
            border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
            borderRadius: 10,
            padding: 4,
            gap: 4,
          }}
        >
          {["cumulative", "daily"].map((v) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                padding: "4px 10px",
                border: "none",
                borderRadius: 6,
                background: view === v ? "var(--od-c-ffffff, #fff)" : "transparent",
                boxShadow:
                  view === v
                    ? "0px 1px 3px 0px rgba(10,13,18,0.1), 0px 1px 2px -1px rgba(10,13,18,0.1)"
                    : "none",
                cursor: "pointer",
                fontWeight: 600,
                color: view === v ? "var(--od-c-414651, #414651)" : "var(--od-c-717680, #717680)",
                fontSize: 13,
                lineHeight: "20px",
                textTransform: "capitalize",
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div
        style={{
          display: "flex",
          gap: 16,
          marginBottom: 20,
        }}
      >
        {actualToDate !== null && (
          <StatCard
            label="Actual to Date"
            value={formatCurrency(actualToDate)}
            color={DEEP_GREEN}
          />
        )}
        <StatCard
          label="Forecast Final"
          value={formatCurrency(forecastFinal)}
          color={LIGHT_GREEN}
        />
        <StatCard
          label="Monthly Target"
          value={formatCurrency(monthlyTarget)}
          color="var(--od-c-535862, #535862)"
        />
        <StatCard
          label="vs. Target"
          value={hasTarget ? `${vsTarget >= 0 ? "+" : ""}${formatCurrency(vsTarget)} (${vsTargetPct}%)` : "—"}
          color={vsColor}
        />
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: 20,
          marginBottom: 12,
          fontSize: 12,
          color: "var(--od-c-717680, #717680)",
        }}
      >
        {view === "cumulative" ? (
          <>
            <LegendItem color={DEEP_GREEN} label="Actual" type="solid" />
            {forecastHitsTarget && (
              <LegendItem color={RED} label="Below Pace" type="solid" />
            )}
            <LegendItem color={forecastColor} label="Forecast" type="solid" />
            <LegendItem color={GREY_DASH} label="Target Pace" type="dashed" />
            <LegendItem color={forecastColor} label="Confidence Band" type="band" />
          </>
        ) : (
          <>
            <LegendItem color={DEEP_GREEN} label="Actual" type="bar" />
            <LegendItem color={LIGHT_GREEN} label="Forecast" type="bar" />
            <LegendItem color={GREY_DASH} label="Daily Target" type="dashed" />
          </>
        )}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={320}>
        {view === "cumulative" ? (
          <ComposedChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: chartColors.tick }}
              tickLine={false}
              axisLine={{ stroke: chartColors.axis }}
              interval={tickInterval}
            />
            <YAxis
              domain={[0, yMax]}
              tick={{ fontSize: 11, fill: chartColors.tick }}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatCompact}
              width={55}
            />
            <Tooltip
              content={<CustomTooltip forecastColor={forecastColor} />}
              cursor={{ stroke: chartColors.cursor, strokeDasharray: "3 3" }}
            />

            {/* Confidence band */}
            <Area
              type="monotone"
              dataKey="confidenceUpper"
              stroke="none"
              fill={bandColor}
              fillOpacity={1}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="confidenceLower"
              stroke="none"
              fill={chartColors.surface}
              fillOpacity={1}
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* Target pace line */}
            <Line
              type="monotone"
              dataKey="linearPace"
              stroke={GREY_DASH}
              strokeWidth={1.5}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
            />

            {/* Forecast line */}
            <Line
              type="monotone"
              dataKey="forecast"
              stroke={forecastColor}
              strokeWidth={2.5}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* Actual line — above pace (green) */}
            <Line
              type="monotone"
              dataKey="actualAbove"
              stroke={DEEP_GREEN}
              strokeWidth={3}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* Actual line — below pace (red) */}
            <Line
              type="monotone"
              dataKey="actualBelow"
              stroke={RED}
              strokeWidth={3}
              dot={false}
              connectNulls={false}
              isAnimationActive={false}
            />

            {/* Today line */}
            {todayDay && (
              <ReferenceLine
                x={`${month + 1}/${todayDay}`}
                stroke={TODAY_COLOR}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "Today",
                  position: "insideTopRight",
                  fill: TODAY_COLOR,
                  fontSize: 11,
                  fontWeight: 600,
                  offset: 8,
                }}
              />
            )}

            {/* Target achieved crossing */}
            {targetCrossDay && (() => {
              const crossPt = data[targetCrossDay - 1];
              const crossY = crossPt
                ? (crossPt.actual ?? crossPt.forecast ?? monthlyTarget)
                : monthlyTarget;
              return (
                <ReferenceDot
                  x={`${month + 1}/${targetCrossDay}`}
                  y={crossY}
                  r={5}
                  fill="#28a745"
                  stroke={chartColors.surface}
                  strokeWidth={2}
                  label={{
                    value: "Target Hit",
                    position: "top",
                    fill: "#28a745",
                    fontSize: 11,
                    fontWeight: 600,
                    offset: 8,
                  }}
                />
              );
            })()}

            {/* Settlement day dots */}
            {data
              .filter((d) => d.isSettlement && d.actual !== null)
              .map((d) => (
                <ReferenceDot
                  key={`settle-${d.day}`}
                  x={d.label}
                  y={d.actual}
                  r={3}
                  fill={DEEP_GREEN}
                  stroke={chartColors.surface}
                  strokeWidth={1.5}
                />
              ))}
          </ComposedChart>
        ) : (
          <ComposedChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartColors.grid}
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: chartColors.tick }}
              tickLine={false}
              axisLine={{ stroke: chartColors.axis }}
              interval={tickInterval}
            />
            <YAxis
              domain={[0, dailyMax]}
              tick={{ fontSize: 11, fill: chartColors.tick }}
              tickLine={false}
              axisLine={false}
              tickFormatter={formatCompact}
              width={55}
            />
            <Tooltip
              content={<DailyTooltip />}
              cursor={{ fill: "rgba(0,0,0,0.03)" }}
            />

            <Bar dataKey="dailyRevenue" radius={[3, 3, 0, 0]} maxBarSize={18}>
              {data.map((entry) => (
                <Cell
                  key={`bar-${entry.day}`}
                  fill={
                    entry.actual !== null ? DEEP_GREEN : LIGHT_GREEN
                  }
                  fillOpacity={entry.dailyRevenue === 0 ? 0 : 0.85}
                />
              ))}
            </Bar>

            {/* Daily target pace line */}
            <ReferenceLine
              y={dailyTargetPace}
              stroke={GREY_DASH}
              strokeDasharray="6 4"
              strokeWidth={1.5}
              label={{
                value: `Target: ${formatCompact(dailyTargetPace)}/day`,
                position: "right",
                fill: GREY_DASH,
                fontSize: 11,
              }}
            />

            {/* Today line */}
            {todayDay && (
              <ReferenceLine
                x={`${month + 1}/${todayDay}`}
                stroke={TODAY_COLOR}
                strokeDasharray="4 4"
                strokeWidth={1.5}
              />
            )}
          </ComposedChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div
    style={{
      flex: 1,
      padding: "10px 14px",
      backgroundColor: "var(--od-c-fafafa, #fafafa)",
      borderRadius: 8,
      border: "1px solid var(--od-c-e9eaeb, #e9eaeb)",
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: "var(--od-c-717680, #717680)",
        marginBottom: 2,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 600, color }}>{value}</div>
  </div>
);

const LegendItem = ({ color, label, type }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    {type === "solid" && (
      <div
        style={{
          width: 16,
          height: 3,
          backgroundColor: color,
          borderRadius: 2,
        }}
      />
    )}
    {type === "dashed" && (
      <svg width="16" height="3">
        <line
          x1="0"
          y1="1.5"
          x2="16"
          y2="1.5"
          stroke={color}
          strokeWidth="2"
          strokeDasharray="4 3"
        />
      </svg>
    )}
    {type === "band" && (
      <div
        style={{
          width: 16,
          height: 10,
          backgroundColor: color,
          borderRadius: 2,
          opacity: 0.3,
        }}
      />
    )}
    {type === "bar" && (
      <div
        style={{
          width: 8,
          height: 10,
          backgroundColor: color,
          borderRadius: 2,
        }}
      />
    )}
    <span>{label}</span>
  </div>
);

export default RevenuePacingChart;
